import { useContext } from 'react';
import { useIntl } from 'react-intl';

import { Box } from '@/components/Box';
import { Modal } from '@/components/Modal';
import { SectionHeader } from '@/components/SectionHeader';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@/components/Table/Table';
import { Typography } from '@/components/Typography';
import { getRegionFromLocale } from '@/lib/regions';
import type { SetState } from '@/lib/types';
import { LocaleContext } from '@/providers/LocaleProvider';
import { useRegionProvider } from '@/providers/RegionProvider';

import { getDownloadBreitlingSizingToolHref } from '../../helper';
import { tableBodyData_INCH, tableBodyData_MM } from './fixtures';

import * as M from './messages';
import * as S from './SizesModal.styles';

export interface SizesModalProps {
  isOpen: boolean;
  setIsOpen: SetState<boolean>;
}

export const SizesModal = ({ isOpen, setIsOpen }: SizesModalProps) => {
  const { formatMessage } = useIntl();
  const locale = useContext(LocaleContext);
  const region = getRegionFromLocale(locale);
  const { unit } = useRegionProvider();
  const languageCode = region.locale.slug;
  const tableData = unit === 'mm' ? tableBodyData_MM : tableBodyData_INCH;

  return (
    <Modal
      data-test-id='SizesModalModal'
      gutters={false}
      isOpen={isOpen}
      maxWidth='sm'
      onClose={() => setIsOpen(false)}
    >
      <Modal.Header
        data-test-id='SizesModalModalHeader'
        divider
        onClose={() => setIsOpen(false)}
      >
        <Box mb={2}>
          <SectionHeader
            data-test-id='SizesModalSectionHeader'
            disableGutters
            numberOfColoredWords={1}
            sizeDesktop={24}
            sizeMobile={24}
            text={formatMessage(M.defined.breitlingStrapSize)}
          />
        </Box>
      </Modal.Header>
      <Modal.Body data-test-id='SizesModalModalBody'>
        <S.BodyWrapper>
          <Typography data-test-id='SizesModalTopDescription' mb={4}>
            {M.TopDescription(getDownloadBreitlingSizingToolHref(languageCode))}
          </Typography>
          <SectionHeader
            data-test-id='SizesModalHowToMeasureSectionHeader'
            numberOfColoredWords={2}
            sizeDesktop={20}
            sizeMobile={20}
            text={formatMessage(M.defined.howToMeasure)}
            textAlign='center'
          />

          <Table>
            <TableHead>
              <TableRow>
                <TableCell
                  align='center'
                  component='th'
                  scope='col'
                  width='34%'
                >
                  <Typography scale='h5' textTransform='uppercase'>
                    {formatMessage(M.defined.strapSize)}
                  </Typography>
                </TableCell>
                <TableCell
                  align='center'
                  component='th'
                  scope='col'
                  width='33%'
                >
                  <Typography scale='h5' textTransform='uppercase'>
                    {formatMessage(M.defined.rubberSize)}
                  </Typography>
                </TableCell>
                <TableCell
                  align='center'
                  component='th'
                  scope='col'
                  width='33%'
                >
                  <Typography scale='h5' textTransform='uppercase'>
                    {formatMessage(M.defined.leatherSize)}
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tableData.map(({ leftCol, middleCol, rightCol }) => (
                <TableRow key={formatMessage(leftCol)}>
                  <TableCell align='center' lightGrey>
                    <Typography scale='h5' textTransform='uppercase'>
                      {formatMessage(leftCol)}
                    </Typography>
                  </TableCell>
                  <TableCell align='center'>
                    <Typography scale='h5' textTransform='uppercase'>
                      {middleCol ? formatMessage(middleCol) : ''}
                    </Typography>
                  </TableCell>
                  <TableCell align='center'>
                    <Typography scale='h5' textTransform='uppercase'>
                      {formatMessage(rightCol)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Typography data-test-id='SizesModalMiddleDescription' my={2}>
            {M.MiddleDescription}
          </Typography>
          <Box data-test-id='SizesModalBox' mb={2}></Box>

          <SectionHeader
            data-test-id='SizesModalNeedARulerSectionHeader'
            numberOfColoredWords={3}
            sizeDesktop={20}
            sizeMobile={20}
            text={formatMessage(M.defined.needARuler)}
            textAlign='center'
          />
          <S.SizingRulerMobileSvg data-test-id='SizesModalSizingRulerMobileSvg' />
          <S.SizingRulerDesktopSvg data-test-id='SizesModalSizingRulerDesktopSvg' />
          {/* TODO implement opening different 'Breitling sizing tool' PDF documents depending on the current language */}
          <S.PrintButton
            data-test-id='SizesModalPrintButton'
            label={formatMessage(M.defined.printTheRuler)}
            linkProps={{
              href: getDownloadBreitlingSizingToolHref(languageCode),
            }}
            mb={2}
            openUrlInNewTab={true}
          />
          <Typography data-test-id='SizesModalBottomDescription'>
            {M.BottomDescription}
          </Typography>
        </S.BodyWrapper>
      </Modal.Body>
    </Modal>
  );
};
